import { useTranslation } from 'react-i18next';
import { FilePicker, Label, LoadingStatus } from '@shared/ui';
import { validateHelpdeskAttachments } from '../api/attachments';

const acceptedFileTypes =
  '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Helpdesk owns file policy and submission; shared FilePicker owns selection and presentation. */
export function HelpdeskAttachmentInput({
  id,
  files,
  disabled,
  uploading = false,
  compact = false,
  onChange,
}: {
  id: string;
  files: File[];
  disabled: boolean;
  uploading?: boolean;
  compact?: boolean;
  onChange: (files: File[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      <Label htmlFor={id}>{t('helpdesk.attachments.label', 'Files')}</Label>
      <FilePicker
        id={id}
        multiple
        files={files}
        onFilesChange={onChange}
        disabled={disabled || uploading}
        accept={acceptedFileTypes}
        chooseLabel={t('helpdesk.attachments.browse', 'Browse files')}
        changeLabel={t('helpdesk.attachments.addMore', 'Add more files')}
        helperText={t(
          'helpdesk.attachments.limits',
          'PDF, JPEG, PNG, WebP, DOC or DOCX. Up to 5 files, 5 MB each.'
        )}
        previewAlt={t('helpdesk.attachments.preview', 'Selected image preview')}
        removeLabel={(file) =>
          t('helpdesk.attachments.remove', 'Remove {{fileName}}', { fileName: file.name })
        }
        error={
          validateHelpdeskAttachments(files)
            ? undefined
            : t(
                'helpdesk.attachments.invalid',
                'Choose PDF, JPEG, PNG, WebP, DOC or DOCX files (up to 5 files, 5 MB each).'
              )
        }
      />
      {uploading ? (
        <LoadingStatus
          label={t('helpdesk.attachments.uploadingFiles', 'Uploading {{count}} file(s)…', {
            count: files.length,
          })}
        />
      ) : null}
    </div>
  );
}
